package com.eladfinish.bible.contest.features.installTime.domain.usecase

import com.eladfinish.bible.contest.features.installTime.domain.config.TimeGateConfig
import com.eladfinish.bible.contest.features.installTime.domain.model.BlockReason
import com.eladfinish.bible.contest.features.installTime.domain.model.InstallEvent
import com.eladfinish.bible.contest.features.installTime.domain.model.TimeGateResult
import com.eladfinish.bible.contest.features.installTime.domain.repository.InstallInfoRepository

class TimeGateUseCase(
    private val repository: InstallInfoRepository,
    private val defaultConfig: TimeGateConfig,
    private val nowMillis: () -> Long,
) {

    operator fun invoke(
        requiredMinutesOverride: Int? = null,
    ): TimeGateResult {

        if (!defaultConfig.enabled) {
            return TimeGateResult.Blocked(
                reason = BlockReason.FEATURE_DISABLED,
                remainingMillis = Long.MAX_VALUE,
                event = InstallEvent.NONE,
            )
        }

        val installInfo = repository.getInstallInfo()
            ?: return TimeGateResult.Blocked(
                reason = BlockReason.MISSING_INSTALL_INFO,
                remainingMillis = Long.MAX_VALUE,
                event = InstallEvent.NONE,
            )

        val now = nowMillis()

        /* ----------------------------
         * Rule 1: time since install
         * ---------------------------- */

        val requiredInstallMinutes =
            requiredMinutesOverride ?: defaultConfig.minutesSinceInstall

        val requiredInstallMillis =
            requiredInstallMinutes * 60L * 1000L

        val elapsedSinceInstall =
            now - installInfo.firstInstallTimeMillis

        if (elapsedSinceInstall < requiredInstallMillis) {
            return TimeGateResult.Blocked(
                reason = BlockReason.NOT_ENOUGH_TIME_SINCE_INSTALL,
                remainingMillis = requiredInstallMillis - elapsedSinceInstall,
                event = InstallEvent.FIRST_INSTALL,
            )
        }

        /* ----------------------------
         * Rule 2: time since update (optional)
         * ---------------------------- */

        if (defaultConfig.minutesSinceUpdate > 0) {
            val requiredUpdateMillis =
                defaultConfig.minutesSinceUpdate * 60L * 1000L

            val elapsedSinceUpdate =
                now - installInfo.lastUpdateTimeMillis

            if (elapsedSinceUpdate < requiredUpdateMillis) {
                return TimeGateResult.Blocked(
                    reason = BlockReason.NOT_ENOUGH_TIME_SINCE_UPDATE,
                    remainingMillis = requiredUpdateMillis - elapsedSinceUpdate,
                    event = InstallEvent.UPDATE,
                )
            }
        }

        return TimeGateResult.Allowed(
            event = installInfo.event
        )
    }
}
